import { NodeData } from './types';

export type NavFilter =
  | 'nav-overview'
  | 'nav-modules'
  | 'nav-workflows'
  | 'nav-screens'
  | 'nav-roles'
  | 'nav-integrations'
  | 'nav-dependencies'
  | 'nav-progress'
  | 'nav-docs';

const moduleCategories = new Set(['Module', 'Admin', 'Support']);

export function nodeMatchesSearch(node: NodeData, searchQuery: string) {
  const query = searchQuery.trim().toLowerCase();

  if (!query) {
    return true;
  }

  const searchableText = [
    node.title,
    node.description,
    node.category,
    node.status,
    node.priority,
    node.owner,
    node.lane,
    node.notes,
    ...node.relatedScreens,
    ...node.childScreens.flatMap((screen) => [
      screen.label,
      screen.description,
      screen.status,
      screen.priority,
      screen.owner,
    ]),
    ...node.dependencies.flatMap((dependency) => [dependency.label, dependency.status]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return searchableText.includes(query);
}

export function nodeMatchesNavFilter(node: NodeData, navFilter: NavFilter) {
  switch (navFilter) {
    case 'nav-modules':
      return moduleCategories.has(node.category);
    case 'nav-workflows':
      return (
        node.category === 'Workflow' ||
        node.title.toLowerCase().includes('workflow') ||
        node.dependencies.some((dependency) => dependency.label.toLowerCase().includes('workflow'))
      );
    case 'nav-screens':
      return node.childScreens.length > 0;
    case 'nav-roles':
      return node.category === 'Role' || Boolean(node.owner);
    case 'nav-integrations':
      return (
        node.category === 'Integration' ||
        node.dependencies.some((dependency) => dependency.label.toLowerCase().includes('integration'))
      );
    case 'nav-dependencies':
      return node.dependencies.length > 0;
    case 'nav-progress':
      return true;
    case 'nav-docs':
      return node.notes.trim().length > 0 || node.relatedScreens.length > 0;
    case 'nav-overview':
    default:
      return true;
  }
}

export function filterNodes(
  nodes: NodeData[],
  searchQuery: string,
  activeLanes: string[],
  navFilter: NavFilter
) {
  return nodes.filter(
    (node) =>
      !node.archivedAt &&
      activeLanes.includes(node.lane) &&
      nodeMatchesSearch(node, searchQuery) &&
      nodeMatchesNavFilter(node, navFilter)
  );
}
